import { COIN_MAP } from './coingecko';
import { TX_TYPES, INCOME_TYPES } from './transactionTypes';

// Alle bekannten Typen (für Validierung)
const KNOWN_TYPES = new Set(Object.keys(TX_TYPES));

// Typen, die ein Coin-Symbol benötigen (kein Swap)
const REQUIRES_SYM = new Set([
  'kauf', 'verkauf', 'einzahlung', 'auszahlung', 'gebuehr', 'burn', 'bridge',
  'futures_open', 'futures_close', 'liquidation', 'margin',
  ...INCOME_TYPES,
]);

/**
 * Validiert normalisierte Transaktionen und berechnet einen Confidence-Score pro TX.
 * Input:  Array von TX-Objekten (sym, typ, datum, menge, preis, gebuehr, symOut/In, mengeOut/In)
 * Output: gleiches Array, jedes TX ergänzt um _issues[], _status, _confidence
 */
export function validateTransactions(txs) {
  const seen = new Set();

  // Pre-Pass: doppelte Zeitstempel über alle TXs erkennen
  const timestampCount = new Map();
  txs.forEach(tx => {
    const key = `${tx.datum || ''}_${tx.zeit || ''}`;
    if (key !== '_') timestampCount.set(key, (timestampCount.get(key) || 0) + 1);
  });

  return txs.map(tx => {
    const issues    = [];
    let confidence  = tx._confidence ?? 1.0;

    // ── Datum ─────────────────────────────────────────────────────────────────
    if (!tx.datum || !/^\d{4}-\d{2}-\d{2}$/.test(tx.datum)) {
      issues.push({ level: 'error', msg: 'Ungültiges Datum' });
      confidence -= 0.30;
    } else {
      const d = new Date(tx.datum);
      if (isNaN(d.getTime())) {
        issues.push({ level: 'error', msg: 'Datum nicht parsebar' });
        confidence -= 0.30;
      } else if (d > new Date()) {
        issues.push({ level: 'warning', msg: 'Datum liegt in der Zukunft' });
        confidence -= 0.15;
      } else if (d.getFullYear() < 2009) {
        issues.push({ level: 'warning', msg: 'Datum vor Bitcoin-Entstehung' });
        confidence -= 0.10;
      }
    }

    // ── Typ ───────────────────────────────────────────────────────────────────
    if (!tx.typ) {
      issues.push({ level: 'error', msg: 'Transaktionstyp fehlt' });
      confidence -= 0.30;
    } else if (!KNOWN_TYPES.has(tx.typ)) {
      issues.push({ level: 'warning', msg: `Unbekannter Typ: ${tx.typ}` });
      confidence -= 0.15;
    }

    // ── Swap ──────────────────────────────────────────────────────────────────
    if (tx.typ === 'swap') {
      if (!tx.symOut || !tx.symIn) {
        issues.push({ level: 'error', msg: 'Swap: Coin-Symbol fehlt' });
        confidence -= 0.25;
      }
      if (!tx.mengeOut || tx.mengeOut <= 0) {
        issues.push({ level: 'error', msg: 'Swap: Abgabemenge fehlt' });
        confidence -= 0.25;
      }
      if (!tx.mengeIn || tx.mengeIn <= 0) {
        issues.push({ level: 'error', msg: 'Swap: Empfangsmenge fehlt' });
        confidence -= 0.25;
      }
      if (tx.symOut && !COIN_MAP[tx.symOut.toUpperCase()]) {
        issues.push({ level: 'warning', msg: `Unbekannter Coin: ${tx.symOut}` });
        confidence -= 0.07;
      }
      if (tx.symIn && !COIN_MAP[tx.symIn.toUpperCase()]) {
        issues.push({ level: 'warning', msg: `Unbekannter Coin: ${tx.symIn}` });
        confidence -= 0.07;
      }

    // ── Transfer / Einzahlung / Auszahlung ────────────────────────────────────
    } else if (tx.typ === 'einzahlung' || tx.typ === 'auszahlung' || tx.typ === 'bridge') {
      if (!tx.menge || tx.menge <= 0) {
        issues.push({ level: 'error', msg: 'Menge fehlt oder ist 0' });
        confidence -= 0.25;
      }
      checkSym(tx, issues, confidence);
      confidence = applySymCheck(tx, issues, confidence);

    // ── Futures / Margin (können 0€ haben — kein Fehler) ─────────────────────
    } else if (['futures_open', 'futures_close', 'margin', 'liquidation'].includes(tx.typ)) {
      if (!tx.menge || tx.menge <= 0) {
        issues.push({ level: 'warning', msg: 'Menge fehlt oder ist 0' });
        confidence -= 0.10;
      }
      confidence = applySymCheck(tx, issues, confidence);

    // ── Gebühr (kein Preis erforderlich) ─────────────────────────────────────
    } else if (tx.typ === 'gebuehr') {
      confidence = applySymCheck(tx, issues, confidence);

    // ── Alle anderen (kauf, verkauf, staking, airdrop, mining usw.) ──────────
    } else if (REQUIRES_SYM.has(tx.typ)) {
      if (!tx.menge || tx.menge <= 0) {
        issues.push({ level: 'error', msg: 'Menge fehlt oder ist 0' });
        confidence -= 0.25;
      }
      if (tx.typ === 'kauf' || tx.typ === 'verkauf') {
        if (!tx.preis || tx.preis <= 0) {
          issues.push({ level: 'error', msg: 'Preis fehlt oder ist 0' });
          confidence -= 0.20;
        } else if (tx.preis > 10_000_000) {
          issues.push({ level: 'warning', msg: 'Ungewöhnlich hoher Gesamtpreis' });
          confidence -= 0.05;
        }
      }
      // Einkommenstypen: preis = 0 ist OK (Marktwert zum Zeitpunkt unbekannt)
      confidence = applySymCheck(tx, issues, confidence);
    }

    // ── Doppelter Zeitstempel ─────────────────────────────────────────────────
    const tsKey = `${tx.datum || ''}_${tx.zeit || ''}`;
    if (tx.datum && (timestampCount.get(tsKey) || 0) > 1) {
      // INFO statt warning + KEIN Konfidenz-Abzug: Ledger/On-Chain-Exporte buchen
      // mehrere Bewegungen mit identischem Zeitstempel (Batch) — das ist normal,
      // kein Datenfehler. Der Datencheck-Banner (hasSuspiciousTimestamps) liest
      // weiterhin den `code`, bleibt also funktionsfähig.
      issues.push({ level: 'info', msg: 'Gleicher Zeitstempel wie andere TX', code: 'SUSPICIOUS_TIMESTAMP' });
    }

    // ── Gebühr-Plausibilität ──────────────────────────────────────────────────
    if ((tx.gebuehr ?? 0) < 0) {
      issues.push({ level: 'warning', msg: 'Negative Gebühr' });
      confidence -= 0.05;
    }

    // ── Duplikatserkennung (Dust ausgenommen) ─────────────────────────────────
    // Mikro-Eingänge (Staub, z.B. 0.000001 XRP aus Address-Poisoning / Reward-Dust)
    // wiederholen sich real identisch — das ist KEIN Doppel-Import. Ohne Ausnahme
    // würden sie dauerhaft als „mögliches Duplikat" (−0.40) geflaggt. Schwelle
    // deckt sich mit SCAM_DUST_MICRO_MENGE (txView): Einzahlung < 0.001 = Dust.
    const isDustInflow = tx.typ === 'einzahlung' && Number(tx.menge) > 0 && Number(tx.menge) < 0.001;
    if (!isDustInflow) {
      // 1e8 statt 1e6: BTC hat 8 Dezimalstellen — 1e6 rundet Satoshi-Beträge auf 0
      const dupeKey = tx.typ === 'swap'
        ? `${tx.datum}_${tx.symOut ?? tx.coinOut}_${tx.symIn ?? tx.coinIn}_swap_${Math.round((tx.mengeOut || 0) * 1e8)}`
        : `${tx.datum}_${tx.sym ?? tx.coin}_${tx.typ}_${Math.round((tx.menge || 0) * 1e8)}_${Math.round((tx.preis || 0) * 100)}`;
      if (seen.has(dupeKey)) {
        issues.push({ level: 'warning', msg: 'Mögliches Duplikat' });
        confidence -= 0.40;
      } else {
        seen.add(dupeKey);
      }
    }

    const hasError   = issues.some(i => i.level === 'error');
    const hasWarning = issues.some(i => i.level === 'warning');
    const status     = hasError ? 'error' : hasWarning ? 'warning' : 'ok';

    // Floor: keine Validierungs-Issues → mindestens 0.95. Der vorgelagerte
    // _confidence-Wert (LLM-Selbsteinschaetzung bei KI-Import, fehlend bei
    // deterministischen Parsern) ist als Vertrauenssignal unbrauchbar — das
    // Validator-Urteil ist die verlaessliche Quelle.
    if (status === 'ok') confidence = Math.max(confidence, 0.95);

    return {
      ...tx,
      _issues:     issues,
      _status:     status,
      _confidence: Math.max(0, Math.min(1, confidence)),
    };
  });
}

function applySymCheck(tx, issues, confidence) {
  const sym = tx.sym || tx.coin;
  if (!sym) {
    issues.push({ level: 'error', msg: 'Coin-Symbol fehlt' });
    return confidence - 0.25;
  }
  if (!COIN_MAP[sym.toUpperCase()]) {
    // Reine INFO: unbekannter/neuer Token (z.B. SPL-Coin ohne CoinGecko-Listing) —
    // bedeutet nur "kein EUR-Preis verfügbar", KEIN Datenfehler. Darf weder die
    // Konfidenz senken noch das Review-Flag (warning-Status) auslösen.
    issues.push({ level: 'info', msg: `Coin nicht in Datenbank: ${sym}` });
    return confidence;
  }
  return confidence;
}

// eslint-disable-next-line no-unused-vars
function checkSym(tx, issues, confidence) {
  // helper kept for clarity – actual work done in applySymCheck
}

/**
 * Erkennt temporale Unmöglichkeiten: Transaktionen die den Coin-Saldo negativ machen.
 * Gibt ein Array von { txId, issue } zurück zum Mergen in die TX-_issues.
 * Wird optional nach validateTransactions() aufgerufen.
 */
export function detectTemporalIssues(validatedTxs) {
  const balances = {};
  const problems = [];

  // Chronologisch sortiert prüfen
  const sorted = [...validatedTxs].sort((a, b) => {
    const da = `${a.datum || ''}T${a.zeit || '00:00:00'}`;
    const db = `${b.datum || ''}T${b.zeit || '00:00:00'}`;
    return da.localeCompare(db);
  });

  for (const tx of sorted) {
    const id = tx._docId || tx._editId != null ? String(tx._editId ?? tx._docId) : null;
    if (!id) continue;

    if (tx.typ === 'kauf' || tx.typ === 'einzahlung' || (TX_TYPES[tx.typ]?.acquires)) {
      const coin = tx.coin || tx.sym;
      if (coin && tx.menge > 0) balances[coin] = (balances[coin] || 0) + tx.menge;
    } else if (tx.typ === 'verkauf' || tx.typ === 'auszahlung' || (TX_TYPES[tx.typ]?.disposes)) {
      const coin = tx.coin || tx.sym;
      if (coin && tx.menge > 0) {
        const bal = (balances[coin] || 0) - tx.menge;
        balances[coin] = bal;
        if (bal < -1e-9) {
          problems.push({
            txId: id,
            issue: { level: 'warning', msg: `Saldo würde negativ: ${coin} (${bal.toFixed(8)})`, code: 'NEGATIVE_BALANCE' },
          });
        }
      }
    } else if (tx.typ === 'swap') {
      const coinOut = tx.coinOut || tx.symOut;
      const coinIn  = tx.coinIn  || tx.symIn;
      if (coinOut && tx.mengeOut > 0) {
        const bal = (balances[coinOut] || 0) - tx.mengeOut;
        balances[coinOut] = bal;
        if (bal < -1e-9) {
          problems.push({
            txId: id,
            issue: { level: 'warning', msg: `Saldo würde negativ: ${coinOut} (${bal.toFixed(8)})`, code: 'NEGATIVE_BALANCE' },
          });
        }
      }
      if (coinIn && tx.mengeIn > 0) balances[coinIn] = (balances[coinIn] || 0) + tx.mengeIn;
    }
  }

  return problems;
}

/**
 * Berechnet den Gesamt-Confidence-Score eines Imports (Mittelwert aller TX-Scores).
 */
export function computeOverallConfidence(validatedTxs) {
  if (!validatedTxs.length) return 0;
  const sum = validatedTxs.reduce((acc, tx) => acc + (tx._confidence ?? 0), 0);
  return sum / validatedTxs.length;
}
